import React, { useEffect, useState } from 'react'
import {
  Card, Tabs, Form, Input, InputNumber, Switch, Button, Table, Space,
  Modal, message, Tag, Popconfirm, Typography, Divider,
} from 'antd'
import {
  AdminAIModel, SettingItem, getSystemApiConfig, updateSystemApiConfig,
  listModels, createModel, updateModel, deleteModel, setDefaultModel, testModel,
  getSettings, updateSettings,
} from '../services/aiConfigAdminService'

const { Title, Text } = Typography

/** 统一 API 凭证面板 */
const SystemConfigPanel: React.FC = () => {
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [apiKeySet, setApiKeySet] = useState(false)
  const [testing, setTesting] = useState(false)

  const load = async () => {
    const cfg = await getSystemApiConfig()
    setApiKeySet(cfg.apiKeySet)
    form.setFieldsValue({ baseUrl: cfg.baseUrl, apiKey: '' })
  }
  useEffect(() => { load() }, [])

  const onSave = async () => {
    const v = await form.validateFields()
    setLoading(true)
    try {
      await updateSystemApiConfig({ baseUrl: v.baseUrl, apiKey: v.apiKey || undefined })
      message.success('已保存')
      await load()
    } finally { setLoading(false) }
  }

  const onTest = async () => {
    setTesting(true)
    try {
      const r = await testModel()
      r.ok ? message.success(r.message) : message.error(`${r.category}：${r.message}`)
    } finally { setTesting(false) }
  }

  return (
    <Form form={form} layout="vertical" style={{ maxWidth: 560 }}>
      <Form.Item label="统一接口地址 (baseUrl)" name="baseUrl"
        rules={[{ required: true, message: '请填写 baseUrl' }]}>
        <Input placeholder="如 http://api.cutb.cn/" />
      </Form.Item>
      <Form.Item label="API Key" name="apiKey"
        extra={apiKeySet ? '已配置，留空则保持不变' : '尚未配置'}>
        <Input.Password placeholder={apiKeySet ? '留空则不修改' : '请输入 API Key'} autoComplete="new-password" />
      </Form.Item>
      <Space>
        <Button type="primary" loading={loading} onClick={onSave}>保存</Button>
        <Button loading={testing} onClick={onTest} title="会向上游发起一次最小测试请求">测试默认模型连通性</Button>
      </Space>
    </Form>
  )
}

/** 模型档案面板 */
const ModelsPanel: React.FC = () => {
  const [data, setData] = useState<AdminAIModel[]>([])
  const [loading, setLoading] = useState(false)
  const [editing, setEditing] = useState<AdminAIModel | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [form] = Form.useForm()

  const load = async () => {
    setLoading(true)
    try { setData(await listModels()) } finally { setLoading(false) }
  }
  useEffect(() => { load() }, [])

  const openCreate = () => {
    setEditing(null)
    form.resetFields()
    form.setFieldsValue({ provider: 'OpenAI-Compatible', available: true, isDefault: false, maxTokens: 8192, temperature: 1, costMultiplier: 1, sortOrder: 0 })
    setModalOpen(true)
  }
  const openEdit = (r: AdminAIModel) => {
    setEditing(r)
    form.setFieldsValue(r)
    setModalOpen(true)
  }

  const onSubmit = async () => {
    const v = await form.validateFields()
    if (editing?.id) { await updateModel(editing.id, v); message.success('已更新') }
    else { await createModel(v); message.success('已创建') }
    setModalOpen(false)
    await load()
  }

  const onDelete = async (r: AdminAIModel) => {
    try { await deleteModel(r.id!); message.success('已删除'); await load() }
    catch (e: any) { message.error(e?.message || '删除失败') }
  }
  const onSetDefault = async (r: AdminAIModel) => {
    try { await setDefaultModel(r.id!); message.success('已设为默认'); await load() }
    catch (e: any) { message.error(e?.message || '操作失败') }
  }
  const onTestRow = async (r: AdminAIModel) => {
    const hide = message.loading(`测试 ${r.displayName}...`, 0)
    try {
      const res = await testModel(r.modelId)
      hide()
      res.ok ? message.success(res.message) : message.error(`${res.category}：${res.message}`)
    } catch { hide(); message.error('测试失败') }
  }

  const columns = [
    { title: '模型ID', dataIndex: 'modelId' },
    { title: '显示名', dataIndex: 'displayName' },
    { title: 'Provider', dataIndex: 'provider' },
    {
      title: '状态', render: (_: any, r: AdminAIModel) => (
        <Space>
          {r.isDefault && <Tag color="gold">默认</Tag>}
          {r.available ? <Tag color="green">可用</Tag> : <Tag>停用</Tag>}
        </Space>
      ),
    },
    {
      title: '操作', render: (_: any, r: AdminAIModel) => (
        <Space size="small" wrap>
          <Button size="small" onClick={() => openEdit(r)}>编辑</Button>
          <Button size="small" onClick={() => onTestRow(r)}>测试</Button>
          {!r.isDefault && <Button size="small" onClick={() => onSetDefault(r)}>设默认</Button>}
          <Popconfirm title="确认删除该模型？" onConfirm={() => onDelete(r)}>
            <Button size="small" danger disabled={r.isDefault}>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  return (
    <>
      <Space style={{ marginBottom: 12 }}>
        <Button type="primary" onClick={openCreate}>新增模型</Button>
        <Button onClick={load}>刷新</Button>
      </Space>
      <Table rowKey="id" loading={loading} dataSource={data} columns={columns as any} pagination={false} size="small" />

      <Modal open={modalOpen} title={editing ? '编辑模型' : '新增模型'} onCancel={() => setModalOpen(false)} onOk={onSubmit} destroyOnClose width={560}>
        <Form form={form} layout="vertical">
          <Form.Item label="模型ID (调用名)" name="modelId" rules={[{ required: true }]}>
            <Input placeholder="如 gemini-2.5-pro" />
          </Form.Item>
          <Form.Item label="显示名称" name="displayName" rules={[{ required: true }]}>
            <Input placeholder="如 专业模型" />
          </Form.Item>
          <Form.Item label="Provider" name="provider"><Input /></Form.Item>
          <Space size="large" wrap>
            <Form.Item label="最大Token" name="maxTokens"><InputNumber min={1} /></Form.Item>
            <Form.Item label="温度" name="temperature"><InputNumber min={0} max={2} step={0.1} /></Form.Item>
            <Form.Item label="成本倍率" name="costMultiplier"><InputNumber min={0} step={0.1} /></Form.Item>
            <Form.Item label="排序" name="sortOrder"><InputNumber /></Form.Item>
          </Space>
          <Space size="large">
            <Form.Item label="输入价/1k" name="inputPricePer1k"><InputNumber min={0} step={0.001} /></Form.Item>
            <Form.Item label="输出价/1k" name="outputPricePer1k"><InputNumber min={0} step={0.001} /></Form.Item>
          </Space>
          <Space size="large">
            <Form.Item label="可用" name="available" valuePropName="checked"><Switch /></Form.Item>
            <Form.Item label="设为默认" name="isDefault" valuePropName="checked"><Switch /></Form.Item>
          </Space>
          <Form.Item label="描述" name="description"><Input.TextArea rows={2} /></Form.Item>
        </Form>
      </Modal>
    </>
  )
}

/** 其他系统配置面板 */
const SettingsPanel: React.FC = () => {
  const [items, setItems] = useState<SettingItem[]>([])
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)

  const load = async () => {
    const list = await getSettings()
    setItems(list)
    const fv: Record<string, any> = {}
    list.forEach(i => { fv[i.key] = i.encrypted ? '' : i.value })
    form.setFieldsValue(fv)
  }
  useEffect(() => { load() }, [])

  const onSave = async () => {
    const v = await form.getFieldsValue()
    const changes: Record<string, string> = {}
    items.forEach(i => {
      const val = v[i.key]
      if (i.encrypted) { if (val && String(val).trim()) changes[i.key] = String(val) }
      else if (val !== undefined && val !== null) changes[i.key] = String(val)
    })
    setLoading(true)
    try { await updateSettings(changes); message.success('已保存'); await load() }
    finally { setLoading(false) }
  }

  return (
    <Form form={form} layout="vertical" style={{ maxWidth: 560 }}>
      {items.map(i => (
        <Form.Item key={i.key} label={i.description || i.key} name={i.key}
          extra={i.encrypted ? (i.isSet ? `已配置 (${i.value})，留空保持不变` : '未配置') : undefined}>
          {i.encrypted
            ? <Input.Password placeholder="留空则不修改" autoComplete="new-password" />
            : <Input />}
        </Form.Item>
      ))}
      <Button type="primary" loading={loading} onClick={onSave}>保存全部</Button>
    </Form>
  )
}

const AIConfigPage: React.FC = () => (
  <div style={{ padding: 24 }}>
    <Title level={3}>AI 配置管理</Title>
    <Text type="secondary">管理创作调用使用的统一 API 凭证、模型档案与系统配置。</Text>
    <Divider />
    <Card>
      <Tabs
        items={[
          { key: 'api', label: '统一 API 凭证', children: <SystemConfigPanel /> },
          { key: 'models', label: '模型档案', children: <ModelsPanel /> },
          { key: 'settings', label: '系统配置', children: <SettingsPanel /> },
        ]}
      />
    </Card>
  </div>
)

export default AIConfigPage
